import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftOutlined,
  BarChartOutlined,
  ExportOutlined,
  PrinterOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { PageIntro, PageLayout, PagePanel } from '../../../../components';
import { useToast } from '../../../../hooks/toast';
import { useCsvExport } from '../../../../hooks/useCsvExport';
import api from '../../../../services/api';
import { currency } from '../../../../shared/utils/functions';
import {
  formatDate,
  getInitialFinalDateMonth,
} from '../../../../shared/utils/format-date';
import {
  getSelectFinancialPlans,
  getSelectPaymentsMethods,
  getSelectProfessionals,
} from '../../../../http';
import {
  IFinancialPlan,
  IPaymentMethod,
  IProfessional,
} from '../../../../shared/interfaces';
import FinancialAdvancedPrint from './Print';

type TReportKey =
  | 'unified'
  | 'synthetic'
  | 'taxes'
  | 'professionals'
  | 'commissions';

interface IReportConfig {
  key: TReportKey;
  label: string;
  endpoint: string;
  description: string;
  useProfessionalFilter?: boolean;
  useFinancialFilters?: boolean;
}

const REPORTS: IReportConfig[] = [
  {
    key: 'unified',
    label: 'Unificado',
    endpoint: '/financials/reports/unified',
    description:
      'Consolida lançamentos financeiros em uma visão analítica única com valores, descontos, taxas e resultado líquido.',
    useFinancialFilters: true,
  },
  {
    key: 'synthetic',
    label: 'Sintético',
    endpoint: '/financials/reports/synthetic',
    description:
      'Agrupa receitas e despesas por plano financeiro para leitura gerencial mais rápida.',
    useFinancialFilters: true,
  },
  {
    key: 'taxes',
    label: 'Taxas',
    endpoint: '/financials/reports/taxes',
    description:
      'Mostra o impacto das taxas financeiras por forma de pagamento no período selecionado.',
    useFinancialFilters: true,
  },
  {
    key: 'professionals',
    label: 'Por profissionais',
    endpoint: '/financials/reports/professionals',
    description:
      'Resume produção, quantidade de tratamentos e ticket médio por profissional.',
    useProfessionalFilter: true,
  },
  {
    key: 'commissions',
    label: 'Comissões',
    endpoint: '/financials/reports/commissions',
    description:
      'Apura a base de produção e o valor estimado de comissão conforme o percentual cadastrado.',
    useProfessionalFilter: true,
  },
];

const isReportKey = (value?: string): value is TReportKey =>
  REPORTS.some((report) => report.key === value);

const getReportRoute = (reportKey: TReportKey) =>
  `/financial/reports/advanced/${reportKey}`;

const SUMMARY_LABELS: Record<string, string> = {
  total_financials: 'Lançamentos',
  total_entries: 'Entradas',
  total_exits: 'Saídas',
  total_discounts: 'Descontos',
  total_taxes: 'Taxas',
  net_result: 'Resultado líquido',
  total_groups: 'Grupos',
  total_gross: 'Valor bruto',
  total_methods: 'Formas',
  effective_rate: 'Taxa efetiva',
  total_professionals: 'Profissionais',
  total_treatments: 'Tratamentos',
  total_items: 'Itens',
  total_production: 'Produção',
  total_base: 'Base comissionável',
  total_commissions: 'Comissões',
};

const formatSummaryValue = (key: string, value: number) => {
  if (key.includes('rate') || key.includes('percent')) {
    return `${Number(value || 0).toFixed(2)}%`;
  }

  if (
    key.includes('total_financials') ||
    key.includes('total_groups') ||
    key.includes('total_methods') ||
    key.includes('total_professionals') ||
    key.includes('total_treatments') ||
    key.includes('total_items')
  ) {
    return Number(value || 0);
  }

  return currency(Number(value || 0));
};

const getStatusTag = (status?: number) => {
  if (status === 2) return <Tag color="green">Pago</Tag>;
  if (status === 5) return <Tag color="red">Cancelado</Tag>;
  return <Tag color="blue">Aberto</Tag>;
};

const getTypeTag = (type?: number | string) => {
  if (Number(type) === 1) return <Tag color="green">Receita</Tag>;
  return <Tag color="red">Despesa</Tag>;
};

const REPORT_COLUMNS: Record<TReportKey, any[]> = {
  unified: [
    {
      title: 'Código',
      dataIndex: 'code',
      width: 90,
      render: (value: number) => `#${value}`,
    },
    {
      title: 'Emissão',
      dataIndex: 'issue_date',
      width: 120,
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Vencimento',
      dataIndex: 'due_date',
      width: 120,
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Cliente',
      dataIndex: 'client_name',
      width: 220,
      render: (value: string) => value || '-',
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      width: 100,
      render: (value: number) => getTypeTag(value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (value: number) => getStatusTag(value),
    },
    {
      title: 'Plano',
      dataIndex: 'financial_plan_description',
      width: 180,
      render: (value: string) => value || 'Sem plano',
    },
    {
      title: 'Forma',
      dataIndex: 'payment_method_description',
      width: 180,
      render: (value: string) => value || 'Sem forma',
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      width: 240,
    },
    {
      title: 'Valor',
      dataIndex: 'value',
      width: 120,
      render: (value: number) => currency(Number(value || 0)),
    },
    {
      title: 'Desconto',
      dataIndex: 'discount',
      width: 120,
      render: (value: number) => currency(Number(value || 0)),
    },
    {
      title: 'Taxa',
      dataIndex: 'taxe',
      width: 120,
      render: (value: number) => currency(Number(value || 0)),
    },
  ],
  synthetic: [
    {
      title: 'Plano financeiro',
      dataIndex: 'group_name',
    },
    {
      title: 'Tipo',
      dataIndex: 'financial_type',
      width: 120,
      render: (value: string) =>
        value === 'RECEITA' ? (
          <Tag color="green">Receita</Tag>
        ) : (
          <Tag color="red">Despesa</Tag>
        ),
    },
    { title: 'Qtd.', dataIndex: 'quantity', width: 100 },
    {
      title: 'Bruto',
      dataIndex: 'gross_value',
      width: 140,
      render: (value: number) => currency(Number(value || 0)),
    },
    {
      title: 'Descontos',
      dataIndex: 'discount_value',
      width: 140,
      render: (value: number) => currency(Number(value || 0)),
    },
    {
      title: 'Taxas',
      dataIndex: 'taxe_value',
      width: 140,
      render: (value: number) => currency(Number(value || 0)),
    },
  ],
  taxes: [
    { title: 'Forma de pagamento', dataIndex: 'payment_method' },
    { title: 'Qtd.', dataIndex: 'quantity', width: 100 },
    {
      title: 'Base bruta',
      dataIndex: 'gross_value',
      width: 150,
      render: (value: number) => currency(Number(value || 0)),
    },
    {
      title: 'Taxas',
      dataIndex: 'taxe_value',
      width: 150,
      render: (value: number) => currency(Number(value || 0)),
    },
    {
      title: 'Taxa efetiva',
      dataIndex: 'effective_rate',
      width: 130,
      render: (value: number) => `${Number(value || 0).toFixed(2)}%`,
    },
  ],
  professionals: [
    { title: 'Profissional', dataIndex: 'professional_name' },
    {
      title: 'Comissão cadastrada',
      dataIndex: 'commission_percent',
      width: 150,
      render: (value: number) => `${Number(value || 0).toFixed(2)}%`,
    },
    { title: 'Tratamentos', dataIndex: 'total_treatments', width: 120 },
    { title: 'Itens', dataIndex: 'total_items', width: 100 },
    {
      title: 'Produção',
      dataIndex: 'production_value',
      width: 150,
      render: (value: number) => currency(Number(value || 0)),
    },
    {
      title: 'Ticket médio',
      dataIndex: 'average_ticket',
      width: 150,
      render: (value: number) => currency(Number(value || 0)),
    },
  ],
  commissions: [
    { title: 'Profissional', dataIndex: 'professional_name' },
    {
      title: 'Comissão (%)',
      dataIndex: 'commission_percent',
      width: 130,
      render: (value: number) => `${Number(value || 0).toFixed(2)}%`,
    },
    { title: 'Tratamentos', dataIndex: 'total_treatments', width: 120 },
    { title: 'Itens', dataIndex: 'total_items', width: 100 },
    {
      title: 'Base',
      dataIndex: 'base_value',
      width: 150,
      render: (value: number) => currency(Number(value || 0)),
    },
    {
      title: 'Comissão apurada',
      dataIndex: 'commission_value',
      width: 170,
      render: (value: number) => currency(Number(value || 0)),
    },
  ],
};

const EXPORT_HEADERS: Record<TReportKey, string[]> = {
  unified: [
    'Codigo',
    'Emissao',
    'Vencimento',
    'Cliente',
    'Tipo',
    'Status',
    'Plano',
    'Forma',
    'Descricao',
    'Valor',
    'Desconto',
    'Taxa',
  ],
  synthetic: [
    'Plano financeiro',
    'Tipo',
    'Quantidade',
    'Bruto',
    'Descontos',
    'Taxas',
  ],
  taxes: [
    'Forma de pagamento',
    'Quantidade',
    'Base bruta',
    'Taxas',
    'Taxa efetiva',
  ],
  professionals: [
    'Profissional',
    'Comissao cadastrada',
    'Tratamentos',
    'Itens',
    'Producao',
    'Ticket medio',
  ],
  commissions: [
    'Profissional',
    'Comissao (%)',
    'Tratamentos',
    'Itens',
    'Base',
    'Comissao apurada',
  ],
};

const REPORT_FILENAMES: Record<TReportKey, string> = {
  unified: 'relatorio_financeiro_unificado.csv',
  synthetic: 'relatorio_financeiro_sintetico.csv',
  taxes: 'relatorio_financeiro_taxas.csv',
  professionals: 'relatorio_financeiro_profissionais.csv',
  commissions: 'relatorio_financeiro_comissoes.csv',
};

const getStatusLabel = (status?: number) => {
  if (status === 2) return 'Pago';
  if (status === 5) return 'Cancelado';
  return 'Aberto';
};

const getTypeLabel = (type?: number | string) =>
  Number(type) === 1 ? 'Receita' : 'Despesa';

const toCsvValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return '""';

  if (typeof value === 'number') {
    return value.toString().replace('.', ',');
  }

  return `"${String(value).replace(/"/g, '""')}"`;
};

const mapExportRow = (reportKey: TReportKey, item: any) => {
  switch (reportKey) {
    case 'unified':
      return [
        toCsvValue(item.code || ''),
        toCsvValue(item.issue_date ? formatDate(item.issue_date) : ''),
        toCsvValue(item.due_date ? formatDate(item.due_date) : ''),
        toCsvValue(item.client_name || ''),
        toCsvValue(getTypeLabel(item.type)),
        toCsvValue(getStatusLabel(item.status)),
        toCsvValue(item.financial_plan_description || 'Sem plano'),
        toCsvValue(item.payment_method_description || 'Sem forma'),
        toCsvValue(item.description || ''),
        toCsvValue(Number(item.value || 0)),
        toCsvValue(Number(item.discount || 0)),
        toCsvValue(Number(item.taxe || 0)),
      ];
    case 'synthetic':
      return [
        toCsvValue(item.group_name || 'Sem plano'),
        toCsvValue(item.financial_type || ''),
        toCsvValue(Number(item.quantity || 0)),
        toCsvValue(Number(item.gross_value || 0)),
        toCsvValue(Number(item.discount_value || 0)),
        toCsvValue(Number(item.taxe_value || 0)),
      ];
    case 'taxes':
      return [
        toCsvValue(item.payment_method || 'Sem forma'),
        toCsvValue(Number(item.quantity || 0)),
        toCsvValue(Number(item.gross_value || 0)),
        toCsvValue(Number(item.taxe_value || 0)),
        toCsvValue(`${Number(item.effective_rate || 0).toFixed(2)}%`),
      ];
    case 'professionals':
      return [
        toCsvValue(item.professional_name || ''),
        toCsvValue(`${Number(item.commission_percent || 0).toFixed(2)}%`),
        toCsvValue(Number(item.total_treatments || 0)),
        toCsvValue(Number(item.total_items || 0)),
        toCsvValue(Number(item.production_value || 0)),
        toCsvValue(Number(item.average_ticket || 0)),
      ];
    case 'commissions':
      return [
        toCsvValue(item.professional_name || ''),
        toCsvValue(`${Number(item.commission_percent || 0).toFixed(2)}%`),
        toCsvValue(Number(item.total_treatments || 0)),
        toCsvValue(Number(item.total_items || 0)),
        toCsvValue(Number(item.base_value || 0)),
        toCsvValue(Number(item.commission_value || 0)),
      ];
    default:
      return [];
  }
};

const FinancialAdvancedReports: React.FC = () => {
  const navigate = useNavigate();
  const { reportKey } = useParams();
  const { addToast } = useToast();
  const { exportCsv } = useCsvExport();
  const date = getInitialFinalDateMonth();

  const [activeReport, setActiveReport] = useState<TReportKey>(
    isReportKey(reportKey) ? reportKey : 'unified',
  );
  const [date_initial, setDate_initial] = useState<string | null>(date.dateI);
  const [date_final, setDate_final] = useState<string | null>(date.dateF);
  const [professionalId, setProfessionalId] = useState<string | undefined>();
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [financialPlans, setFinancialPlans] = useState<IFinancialPlan[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [financialPlanId, setFinancialPlanId] = useState<string | undefined>();
  const [paymentId, setPaymentId] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [type, setType] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [printOpen, setPrintOpen] = useState(false);

  const activeConfig = useMemo(
    () => REPORTS.find((item) => item.key === activeReport) || REPORTS[0],
    [activeReport],
  );

  const loadProfessionals = useCallback(async () => {
    try {
      const data = await getSelectProfessionals();
      setProfessionals(data);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Não foi possível carregar os profissionais.',
      });
    }
  }, [addToast]);

  const loadFinancialDependencies = useCallback(async () => {
    try {
      const [plansData, methodsData] = await Promise.all([
        getSelectFinancialPlans(),
        getSelectPaymentsMethods(),
      ]);
      setFinancialPlans(plansData);
      setPaymentMethods(methodsData);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description:
          'Não foi possível carregar planos financeiros e formas de pagamento.',
      });
    }
  }, [addToast]);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const payload: Record<string, any> = {
        date_initial,
        date_final,
      };

      if (activeConfig.useProfessionalFilter && professionalId) {
        payload.professional_id = professionalId;
      }

      if (activeConfig.useFinancialFilters) {
        if (financialPlanId) payload.financial_plan_id = financialPlanId;
        if (paymentId) payload.payment_id = paymentId;
        if (status) payload.status = status;
        if (type) payload.type = type;
      }

      const response = await api.post(activeConfig.endpoint, payload);
      setItems(response.data.items || []);
      setSummary(response.data.summary || {});
    } catch {
      addToast({
        type: 'error',
        title: 'Erro',
        description: `Não foi possível carregar o relatório ${activeConfig.label.toLowerCase()}.`,
      });
    } finally {
      setLoading(false);
    }
  }, [
    activeConfig,
    addToast,
    date_final,
    date_initial,
    financialPlanId,
    paymentId,
    professionalId,
    status,
    type,
  ]);

  useEffect(() => {
    if (isReportKey(reportKey)) {
      setActiveReport(reportKey);
      return;
    }

    if (reportKey) {
      navigate(getReportRoute('unified'), { replace: true });
    }
  }, [navigate, reportKey]);

  useEffect(() => {
    loadProfessionals();
    loadFinancialDependencies();
  }, [loadFinancialDependencies, loadProfessionals]);

  useEffect(() => {
    if (!activeConfig.useProfessionalFilter) {
      setProfessionalId(undefined);
    }
    if (!activeConfig.useFinancialFilters) {
      setFinancialPlanId(undefined);
      setPaymentId(undefined);
      setStatus(undefined);
      setType(undefined);
    }
  }, [activeConfig.useFinancialFilters, activeConfig.useProfessionalFilter]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleTabChange = (value: string) => {
    if (!isReportKey(value)) return;
    setActiveReport(value);
    navigate(getReportRoute(value));
  };

  const handleExport = () => {
    exportCsv(
      items,
      EXPORT_HEADERS[activeReport],
      REPORT_FILENAMES[activeReport],
      (item) => mapExportRow(activeReport, item),
    );
  };

  const handlePrint = () => {
    setPrintOpen(true);
  };

  const summaryCards = Object.entries(summary);
  const selectedProfessional = professionals.find(
    (professional) => professional.id === professionalId,
  );
  const selectedFinancialPlan = financialPlans.find(
    (plan) => plan.id === financialPlanId,
  );
  const selectedPaymentMethod = paymentMethods.find(
    (paymentMethod) => paymentMethod.id === paymentId,
  );
  const printFilters = [
    {
      label: 'Período',
      value: `${formatDate(date_initial || '')} a ${formatDate(date_final || '')}`,
    },
    ...(selectedProfessional
      ? [
          {
            label: 'Profissional',
            value: selectedProfessional.name,
          },
        ]
      : []),
    ...(selectedFinancialPlan
      ? [
          {
            label: 'Plano financeiro',
            value: selectedFinancialPlan.description,
          },
        ]
      : []),
    ...(selectedPaymentMethod
      ? [
          {
            label: 'Forma de pagamento',
            value: selectedPaymentMethod.description,
          },
        ]
      : []),
    ...(status
      ? [
          {
            label: 'Status',
            value: status === '2' ? 'Pago' : 'Em aberto',
          },
        ]
      : []),
    ...(type
      ? [
          {
            label: 'Tipo',
            value: type === '1' ? 'Receita' : 'Despesa',
          },
        ]
      : []),
  ];

  return (
    <PageLayout>
      <PageIntro
        badge="Relatórios"
        icon={<BarChartOutlined />}
        title="Relatórios financeiros avançados"
        description={activeConfig.description}
        actions={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
              disabled={!items.length}
            >
              Exportar CSV
            </Button>
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              disabled={!items.length}
            >
              Imprimir
            </Button>
            <Button icon={<RedoOutlined />} onClick={loadReport}>
              Atualizar
            </Button>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/financials')}
            >
              Voltar
            </Button>
          </Space>
        }
      />

      <PagePanel>
        <Card>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Input
                type="date"
                value={date_initial || ''}
                onChange={(e) => setDate_initial(e.target.value)}
              />
            </Col>
            <Col xs={24} md={8}>
              <Input
                type="date"
                value={date_final || ''}
                onChange={(e) => setDate_final(e.target.value)}
              />
            </Col>
            {activeConfig.useProfessionalFilter ? (
              <Col xs={24} md={8}>
                <Select
                  value={professionalId}
                  onChange={(value) => setProfessionalId(value)}
                  allowClear
                  placeholder="Todos os profissionais"
                  style={{ width: '100%' }}
                  options={professionals.map((professional) => ({
                    label: professional.name,
                    value: professional.id,
                  }))}
                />
              </Col>
            ) : null}
            {activeConfig.useFinancialFilters ? (
              <>
                <Col xs={24} md={12} lg={6}>
                  <Select
                    value={financialPlanId}
                    onChange={(value) => setFinancialPlanId(value)}
                    allowClear
                    placeholder="Todos os planos"
                    style={{ width: '100%' }}
                    options={financialPlans.map((plan) => ({
                      label: plan.description,
                      value: plan.id,
                    }))}
                  />
                </Col>
                <Col xs={24} md={12} lg={6}>
                  <Select
                    value={paymentId}
                    onChange={(value) => setPaymentId(value)}
                    allowClear
                    placeholder="Todas as formas"
                    style={{ width: '100%' }}
                    options={paymentMethods.map((method) => ({
                      label: method.description,
                      value: method.id,
                    }))}
                  />
                </Col>
                <Col xs={24} md={12} lg={6}>
                  <Select
                    value={status}
                    onChange={(value) => setStatus(value)}
                    allowClear
                    placeholder="Todos os status"
                    style={{ width: '100%' }}
                    options={[
                      { label: 'Em aberto', value: '1' },
                      { label: 'Pago', value: '2' },
                    ]}
                  />
                </Col>
                <Col xs={24} md={12} lg={6}>
                  <Select
                    value={type}
                    onChange={(value) => setType(value)}
                    allowClear
                    placeholder="Receitas e despesas"
                    style={{ width: '100%' }}
                    options={[
                      { label: 'Somente despesas', value: '2' },
                      { label: 'Somente receitas', value: '1' },
                    ]}
                  />
                </Col>
              </>
            ) : null}
          </Row>
        </Card>
      </PagePanel>

      <PagePanel>
        <Card>
          <Tabs
            activeKey={activeReport}
            onChange={handleTabChange}
            items={REPORTS.map((report) => ({
              key: report.key,
              label: report.label,
            }))}
          />
        </Card>
      </PagePanel>

      {summaryCards.length ? (
        <PagePanel>
          <Row gutter={[16, 16]}>
            {summaryCards.map(([key, value]) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={key}>
                <Card>
                  <Statistic
                    title={SUMMARY_LABELS[key] || key}
                    value={Number(value || 0)}
                    formatter={() =>
                      formatSummaryValue(key, Number(value || 0))
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </PagePanel>
      ) : null}

      <PagePanel>
        <Card>
          <Table
            rowKey={(record, index) =>
              String(
                record.id ||
                  record.professional_id ||
                  record.group_name ||
                  record.payment_method ||
                  index,
              )
            }
            loading={loading}
            dataSource={items}
            columns={REPORT_COLUMNS[activeReport]}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 1100 }}
            locale={{
              emptyText: 'Nenhum dado encontrado para os filtros selecionados.',
            }}
          />
        </Card>
      </PagePanel>

      {printOpen ? (
        <FinancialAdvancedPrint
          open={printOpen}
          close={() => setPrintOpen(false)}
          report={{
            reportKey: activeReport,
            reportLabel: activeConfig.label,
            description: activeConfig.description,
            items,
            summary,
            filters: printFilters,
          }}
        />
      ) : null}
    </PageLayout>
  );
};

export default FinancialAdvancedReports;
